/**
 * ExecutiveBriefsPage Component (BRIEFS-1)
 * AIDLC Administrative Backend
 *
 * The Executive Briefs library: self-contained HTML briefs agents write for the CEO
 * (the first is Sarah's AI Governance Program brief). The list shows each brief's
 * title, author, project, last update and version; opening one shows the brief itself.
 *
 * The brief is agent-written HTML, so it is shown in an iframe with `sandbox=""`:
 * no script, no forms, no popups, and an opaque origin that cannot reach the portal.
 * The API takes a bearer token in a header, which an iframe `src` cannot send, so the
 * HTML is fetched with the signed-in client and set as `srcdoc`, with the server's
 * policy repeated inside it as a meta tag (a `srcdoc` frame has no response headers).
 *
 * Route: /docs/briefs (?brief=EB-xxxxxxxx opens one). Personas: all, executive included.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtDateTime } from '../../utils/formatters';
import { fetchBriefs, fetchBriefHtml } from '../../services/briefService';
import type { ExecutiveBriefMeta } from '../../services/briefService';

const BRIEF_PARAM = 'brief';

/**
 * The server's policy for /api/briefs/:id/html (BRIEF_HTML_CSP in server/src/routes/briefs.ts), less
 * frame-ancestors and sandbox, which a meta tag cannot set (the iframe's own sandbox="" attribute covers the second).
 */
const BRIEF_META_CSP = [
  "default-src 'none'",
  'img-src data:',
  "style-src 'unsafe-inline' https://fonts.googleapis.com",
  'font-src https://fonts.gstatic.com',
  "base-uri 'none'",
  "form-action 'none'",
].join('; ');

/**
 * Puts the policy first in the document's head, so it governs everything after it. The document is parsed
 * (DOMParser runs no script and loads nothing) rather than searched, so a `<head>` in a comment or a
 * string cannot leave the policy outside the real head.
 */
export function withBriefPolicy(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const meta = doc.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = BRIEF_META_CSP;
  doc.head.insertBefore(meta, doc.head.firstChild);
  const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : '';
  return doctype + doc.documentElement.outerHTML;
}

/** The server's `{ error }` reason. The document request keeps its body as text, so a JSON error arrives as a string. */
function serverReason(data: unknown): string | undefined {
  let body = data;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { return undefined; }
  }
  const reason = (body as { error?: unknown } | null)?.error;
  return typeof reason === 'string' && reason ? reason : undefined;
}

function errorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  if (response?.status === 404) return 'That brief no longer exists.';
  if (response?.status === 401 || response?.status === 403) return 'You are not signed in with access to briefs.';
  return serverReason(response?.data) || fallback;
}

function ErrorBanner({ message, onRetry, actionLabel = 'Retry' }: { message: string; onRetry: () => void; actionLabel?: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
      <p className="text-sm text-red-700">{message}</p>
      <button onClick={onRetry} className="mt-2 text-xs text-red-600 hover:underline">
        {actionLabel}
      </button>
    </div>
  );
}

// ─── Viewer ───────────────────────────────────────────────────────

function BriefViewer({ brief, onBack }: { brief: ExecutiveBriefMeta; onBack: () => void }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    fetchBriefHtml(brief.id)
      .then((text) => { if (!cancelled) setHtml(text); })
      .catch((err: unknown) => {
        console.error('[ExecutiveBriefs] Failed to load brief:', brief.id, err);
        if (!cancelled) setError(errorMessage(err, 'Failed to load this brief. Please check the backend connection.'));
      });
    return () => { cancelled = true; };
  }, [brief.id, attempt]);

  const srcDoc = useMemo(() => (html === null ? null : withBriefPolicy(html)), [html]);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs text-ldaja-blue hover:underline">
        ← All briefs
      </button>
      <div>
        <h1 className="text-xl font-bold text-ldaja-black">{brief.title}</h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-gray-500">
          <span>By {brief.authorName}</span>
          <Badge variant="neutral">{brief.project}</Badge>
          <Badge variant="info">v{brief.version}</Badge>
          {brief.backlogItemId && <Badge variant="purple">{brief.backlogItemId}</Badge>}
          <span>Updated {fmtDateTime(brief.updatedAt)}</span>
        </div>
        {brief.summary && <p className="text-sm text-gray-600 mt-3 max-w-3xl">{brief.summary}</p>}
      </div>

      {error && <ErrorBanner message={error} onRetry={() => setAttempt((n) => n + 1)} />}
      {!error && srcDoc === null && <LoadingSpinner message="Loading brief..." />}
      {srcDoc !== null && (
        <iframe
          title={brief.title}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
          className="w-full bg-white border border-gray-200 rounded-lg"
          style={{ height: 'calc(100vh - 260px)', minHeight: 600 }}
        />
      )}
    </div>
  );
}

// ─── List ─────────────────────────────────────────────────────────

function BriefList({ briefs, onOpen }: { briefs: ExecutiveBriefMeta[]; onOpen: (id: string) => void }) {
  return (
    <Card noPadding>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="px-4 py-2 font-medium">Title</th>
              <th className="px-4 py-2 font-medium">Author</th>
              <th className="px-4 py-2 font-medium">Project</th>
              <th className="px-4 py-2 font-medium">Updated</th>
              <th className="px-4 py-2 font-medium text-right">Version</th>
            </tr>
          </thead>
          <tbody>
            {briefs.map((brief) => (
              <tr
                key={brief.id}
                onClick={() => onOpen(brief.id)}
                className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpen(brief.id); }}
                    className="text-left font-medium text-ldaja-black hover:text-ldaja-blue"
                  >
                    {brief.title}
                  </button>
                  {brief.summary && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{brief.summary}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{brief.authorName}</td>
                <td className="px-4 py-3 whitespace-nowrap"><Badge variant="neutral">{brief.project}</Badge></td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDateTime(brief.updatedAt)}</td>
                <td className="px-4 py-3 text-right text-gray-600">v{brief.version}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────

export function ExecutiveBriefsPage() {
  const [briefs, setBriefs] = useState<ExecutiveBriefMeta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get(BRIEF_PARAM);

  const loadBriefs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setBriefs(await fetchBriefs());
    } catch (err) {
      console.error('[ExecutiveBriefs] Failed to load:', err);
      setError(errorMessage(err, 'Failed to load executive briefs. Please check the backend connection.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadBriefs(); }, [loadBriefs]);

  const open = (id: string) => setSearchParams({ [BRIEF_PARAM]: id });
  const close = () => setSearchParams({});

  if (isLoading) return <LoadingSpinner message="Loading executive briefs..." />;
  if (error) return <ErrorBanner message={error} onRetry={loadBriefs} />;

  if (openId) {
    const brief = briefs.find((b) => b.id === openId);
    if (brief) return <BriefViewer brief={brief} onBack={close} />;
    return <ErrorBanner message={`Brief ${openId} was not found. It may have been removed.`} onRetry={close} actionLabel="← All briefs" />;
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl font-bold text-ldaja-black">Executive Briefs</h1>
        <p className="text-sm text-gray-500 mt-1">
          Short reports agents write for the CEO, kept here with every update
        </p>
      </div>

      {briefs.length === 0 ? (
        <Card>
          <EmptyState
            title="No executive briefs yet"
            description="An executive brief is a short, self-contained report an agent writes for the CEO on one topic, such as Sarah's AI Governance Program brief. When an agent publishes one it appears here; each update keeps the same brief and raises its version."
          />
        </Card>
      ) : (
        <BriefList briefs={briefs} onOpen={open} />
      )}
    </div>
  );
}
